import React from 'react';
import {
  forwardRef,
  memo,
  useState,
  useCallback,
  useRef,
  useMemo
} from 'react';
import { DivWithTextAreaSuggestions } from '../DivWithTextAreaSuggestions/DivWithTextAreaSuggestions';
import { AttachmentCarousel } from '../AttachmentCarousel/AttachmentCarousel';
import { classNames } from '../../utils/classNames';
import {
  useCommentFormFileUpload,
  getActionBarConfigWithFileUpload,
  getFileUploadInputProps
} from '../../containers/CommentForm/useCommentFormFileUpload';
import type { ReplyInputProps } from './ReplyInput.types';
import type { CommentMention } from '../../containers/CommentList/CommentList.types';
import './ReplyInput.css';

const ReplyInputComponent = forwardRef<HTMLTextAreaElement, ReplyInputProps>(
  (
    {
      onSubmit,
      placeholder = '@mention or reply',
      className,
      value = '',
      submitOnBlur = false,
      onClearReply,
      actionBarConfig,
      ...props
    },
    ref
  ) => {
    const [mentions, setMentions] = useState<CommentMention[]>([]);
    const formContainerRef = useRef<HTMLDivElement>(null);

    const fileUpload = useCommentFormFileUpload(
      actionBarConfig,
      onClearReply ?? (() => {})
    );

    const handleMentionsChange = useCallback(
      (newMentions: CommentMention[]) => setMentions(newMentions),
      []
    );

    const canSubmit = useCallback(
      (text: string) => {
        const hasText = (text || '').trim().length > 0;
        const hasAttachments = fileUpload
          ? fileUpload.attachmentCount > 0
          : false;
        return hasText || hasAttachments;
      },
      [fileUpload]
    );

    const performSubmit = useCallback(
      (text: string) => {
        onSubmit(
          text,
          mentions,
          fileUpload ? fileUpload.getFiles() : undefined
        );
        fileUpload?.resetAfterSubmit();
      },
      [onSubmit, mentions, fileUpload]
    );

    const handleSubmit = useCallback(
      (submittedValue: string) => {
        if (!canSubmit(submittedValue)) return;
        performSubmit(submittedValue);
      },
      [canSubmit, performSubmit]
    );

    const handleBlur = useCallback(() => {
      if (!submitOnBlur || !canSubmit(value)) return;
      const runAfterFocusSettled = () => {
        const active = document.activeElement;
        if (formContainerRef.current?.contains(active)) return;
        performSubmit(value);
      };
      setTimeout(runAfterFocusSettled, 0);
    }, [value, submitOnBlur, canSubmit, performSubmit]);

    const divProps = useMemo(
      () => ({
        ...props,
        actionBarConfig: fileUpload
          ? getActionBarConfigWithFileUpload(actionBarConfig, fileUpload)
          : actionBarConfig,
        ...(fileUpload ? getFileUploadInputProps(fileUpload) : {})
      }),
      [props, actionBarConfig, fileUpload]
    );

    return (
      <>
        <DivWithTextAreaSuggestions
          {...divProps}
          ref={ref}
          value={value}
          placeholder={placeholder}
          onSubmit={handleSubmit}
          onBlur={handleBlur}
          onMentionsChange={handleMentionsChange}
          wrapperClassName={classNames('reply-input', className)}
          containerRef={formContainerRef}
        />
        {fileUpload?.carouselOpen && fileUpload.attachments.length > 0 && (
          <AttachmentCarousel
            attachments={fileUpload.attachments}
            initialIndex={fileUpload.carouselIndex}
            onClose={() => fileUpload.setCarouselOpen(false)}
          />
        )}
      </>
    );
  }
);

ReplyInputComponent.displayName = 'ReplyInput';

export const ReplyInput = memo(ReplyInputComponent);
